// Author: Ahsan Saleem
/**
 * V2 structural primitives — the layer the audit found missing (324 inline styles,
 * 145 raw `.btn`, 23 status vocabularies, hand-emitted rows). Every V2 screen
 * composes from these so structure, status language and touch targets are solved
 * once. Styling lives in v2.css (token-driven, ≥44px targets); nothing here uses
 * inline styles or raw hex.
 */
import { useRef, useId, cloneElement, isValidElement, type ButtonHTMLAttributes, type ComponentType, type ReactNode, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronRight } from 'lucide-react';
import { Badge, type Tone } from '../../ui/primitives.js';
import { useInspector } from '../../ui/Shell.js';
import { statusOf } from '../status.js';
import { hasResolver, openObject } from '../objects.js';

// Matches lucide-react's icon signature (size is string | number) so any lucide
// icon can be passed as a prop without a cast.
type IconType = ComponentType<{ size?: string | number; className?: string }>;

/* ── Button ──────────────────────────────────────────────────────────────── */
export function Button({ variant = 'default', size = 'md', busy = false, icon: Icon, children, disabled, className = '', ...rest }: {
  variant?: 'primary' | 'default' | 'ghost' | 'danger'; size?: 'md' | 'sm'; busy?: boolean; icon?: IconType;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`v2-btn v2-btn-${variant} v2-btn-${size} ${className}`} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <Loader2 size={15} className="v2-spin" aria-hidden /> : Icon ? <Icon size={15} aria-hidden /> : null}
      {children && <span>{children}</span>}
    </button>
  );
}

/* ── Field ───────────────────────────────────────────────────────────────── */
export function Field({ label, hint, error, required, children }: {
  label: ReactNode; hint?: ReactNode; error?: string | null; required?: boolean; children: ReactNode;
}) {
  // Programmatically associate the label with a single native control child
  // (input/textarea/select), so every field control has an accessible name
  // (WCAG "label"). Composite controls (ObjectPicker/Segmented) are buttons and
  // are labelled by their own aria-label.
  const id = useId();
  const native = isValidElement(children) && typeof (children as ReactElement).type === 'string'
    && ['input', 'textarea', 'select'].includes((children as ReactElement).type as string);
  const control = native ? cloneElement(children as ReactElement, { id }) : children;
  return (
    <div className="v2-field">
      <label className="v2-field-label" htmlFor={native ? id : undefined}>{label}{required && <span className="v2-req" aria-hidden>*</span>}</label>
      {control}
      {error ? <span className="v2-field-error" role="alert">{error}</span> : hint ? <span className="v2-field-hint">{hint}</span> : null}
    </div>
  );
}

/* ── Segmented (accessible tablist with roving focus) ────────────────────── */
export function Segmented<T extends string>({ value, onChange, options, ariaLabel }: {
  value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode; icon?: IconType }>; ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = options.findIndex((o) => o.value === value);
    const next = e.key === 'ArrowRight' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
    onChange(options[next].value);
    ref.current?.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
  };
  return (
    <div className="v2-seg" role="tablist" aria-label={ariaLabel} ref={ref} onKeyDown={onKeyDown}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} role="tab" aria-selected={on} tabIndex={on ? 0 : -1}
            className={`v2-seg-btn${on ? ' on' : ''}`} onClick={() => onChange(o.value)}>
            {o.icon && <o.icon size={14} aria-hidden />}{o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── StatusTag (the one status vocabulary) ───────────────────────────────── */
export function StatusTag({ type, status, subtle }: { type: string; status: string | null | undefined; subtle?: boolean }) {
  const m = statusOf(type, status);
  return <Badge tone={m.tone} subtle={subtle}>{m.label}</Badge>;
}

/* ── ObjectRef (click-through reference, replaces typed opaque keys) ──────────
   Continuous flow: when the target kind is resolvable, the reference opens the
   object INTO the Inspector (a push onto its back-stack) rather than navigating
   away — so drilling related objects never costs you the screen behind. A `to`
   route is the fallback for kinds not yet registered in the object resolver. */
export function ObjectRef({ to, kind, label, icon: Icon, objectKind, objectRef }: {
  to?: string; kind?: string; label: ReactNode; icon?: IconType; objectKind?: string; objectRef?: string;
}) {
  const navigate = useNavigate();
  const inspector = useInspector();
  const resolvable = !!objectKind && !!objectRef && hasResolver(objectKind);
  const inner = (
    <>
      {Icon && <Icon size={13} aria-hidden />}
      {kind && <span className="v2-ref-kind">{kind}</span>}
      <span className="v2-ref-label">{label}</span>
    </>
  );
  if (!to && !resolvable) return <span className="v2-ref v2-ref-static">{inner}</span>;
  const onClick = () => {
    if (resolvable && openObject(inspector, objectKind!, objectRef!)) return;
    if (to) navigate(to);
  };
  return <button className="v2-ref" onClick={onClick}>{inner}<ChevronRight size={12} className="v2-ref-go" aria-hidden /></button>;
}

/* ── Timeline (state / decision history) ─────────────────────────────────── */
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
export interface TimelineEvent { at?: string | null; label: ReactNode; by?: string | null; tone?: Tone; detail?: ReactNode }
export function Timeline({ events }: { events: TimelineEvent[] }) {
  if (!events.length) return null;
  return (
    <ol className="v2-timeline">
      {events.map((e, i) => (
        <li key={i} className={`v2-tl-item${e.tone ? ` tone-${e.tone}` : ''}`}>
          <span className="v2-tl-dot" aria-hidden />
          <div className="v2-tl-body">
            <div className="v2-tl-label">{e.label}{e.by && <span className="v2-tl-by">· {e.by}</span>}</div>
            {e.detail && <div className="v2-tl-detail">{e.detail}</div>}
            {e.at && <time className="v2-tl-at">{fmtWhen(e.at)}</time>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ── KeyFacts (definition list) ──────────────────────────────────────────── */
export function KeyFacts({ facts }: { facts: Array<{ k: ReactNode; v: ReactNode }> }) {
  return (
    <dl className="v2-facts">
      {facts.map((f, i) => (
        <div key={i} className="v2-fact"><dt>{f.k}</dt><dd className="tnum">{f.v}</dd></div>
      ))}
    </dl>
  );
}
